import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { useSimpleAuth } from './SimpleAuthContext';
import {
  BattlePlan,
  PlanSubject,
  ActionItem,
  Participant,
  PlanningStats,
  PlanFilter,
} from '../types/planning.types';

// Company type from FedermanBenjamin (same as budget app)
interface Company {
  id: string;
  CompanyName: string;
  Users: string[];
}

interface PlanningContextType {
  // Company
  companies: Company[];
  currentCompany: Company | null;
  selectCompany: (companyId: string) => void;

  plans: BattlePlan[];
  currentPlan: BattlePlan | null;
  loading: boolean;
  error: string | null;
  stats: PlanningStats;
  filter: PlanFilter;
  setFilter: (filter: PlanFilter) => void;
  fetchPlans: () => Promise<void>;
  fetchPlan: (id: string) => Promise<BattlePlan | null>;
  createPlan: (plan: Omit<BattlePlan, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'createdBy' | 'createdByName'>) => Promise<string>;
  updatePlan: (id: string, updates: Partial<BattlePlan>) => Promise<void>;
  deletePlan: (id: string) => Promise<void>;
  setCurrentPlan: (plan: BattlePlan | null) => void;
  // Subjects
  addSubject: (planId: string, subject: Omit<PlanSubject, 'id' | 'actionItems'>) => Promise<void>;
  updateSubject: (planId: string, subjectId: string, updates: Partial<PlanSubject>) => Promise<void>;
  deleteSubject: (planId: string, subjectId: string) => Promise<void>;
  // Action Items
  addActionItem: (planId: string, subjectId: string, item: Omit<ActionItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateActionItem: (planId: string, subjectId: string, itemId: string, updates: Partial<ActionItem>) => Promise<void>;
  deleteActionItem: (planId: string, subjectId: string, itemId: string) => Promise<void>;
  toggleActionItem: (planId: string, subjectId: string, itemId: string) => Promise<void>;
  // Participants
  addParticipant: (planId: string, participant: Omit<Participant, 'id'>) => Promise<void>;
  removeParticipant: (planId: string, participantId: string) => Promise<void>;
}

const PlanningContext = createContext<PlanningContextType | undefined>(undefined);

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Map a Vue BattlePlan Firestore doc to our React BattlePlan type.
 *
 * Vue fields:
 *   Document.Name, Subject, Participants [{Name}], ActionItems [{Name, completed}],
 *   content, Date (DD-MM-YYYY), _Data
 */
function mapVueDocToPlan(docId: string, data: any, companyId: string): BattlePlan {
  // Parse participants from Vue format: [{Name: string}]
  const participants: Participant[] = (data.Participants || []).map((p: any, idx: number) => ({
    id: p.id || `participant-${idx}`,
    name: p.Name || p.name || '',
    email: p.Email || p.email || '',
    role: p.Role || p.role || '',
  }));

  // Parse action items from Vue format: [{Name: string, completed: boolean}]
  const actionItems: ActionItem[] = (data.ActionItems || []).map((item: any, idx: number) => {
    const now = new Date().toISOString();
    return {
      id: item.id || `action-${idx}`,
      description: item.Name || item.name || '',
      assigneeId: item.AssigneeId || item.assigneeId || '',
      assigneeName: item.AssigneeName || item.assigneeName || '',
      dueDate: item.DueDate || item.dueDate || undefined,
      completedAt: item.completed ? now : undefined,
      isCompleted: !!item.completed,
      priority: item.Priority || item.priority || 'medium',
      notes: item.Notes || item.notes || '',
      createdAt: item.CreatedAt || item.createdAt || now,
      updatedAt: item.UpdatedAt || item.updatedAt || now,
    };
  });

  // Vue stores Subject as a single string; we map it to one PlanSubject
  const subjectText = data.Subject || '';
  const subjects: PlanSubject[] = [];
  if (subjectText || actionItems.length > 0) {
    subjects.push({
      id: 'main-subject',
      title: subjectText || 'נושאים',
      description: '',
      actionItems,
      order: 0,
    });
  }

  // Parse date from Vue format DD-MM-YYYY -> ISO
  let dateStr = '';
  const rawDate = data.Date || '';
  if (rawDate) {
    const parts = rawDate.split('-');
    if (parts.length === 3) {
      // DD-MM-YYYY -> YYYY-MM-DD
      dateStr = `${parts[2]}-${parts[1]}-${parts[0]}`;
    } else {
      dateStr = rawDate;
    }
  }

  // Determine status from Vue data (Vue doesn't have explicit status)
  let status: BattlePlan['status'] = 'active';
  if (data.Status) {
    // If Vue has an explicit status
    const s = typeof data.Status === 'number' ? data.Status : 0;
    if (s === 2) status = 'completed';
    else if (s === 3) status = 'archived';
    else if (s === 0) status = 'draft';
  }

  const planName = data.Document?.Name || data.Name || data.name || 'Unnamed Plan';

  return {
    id: docId,
    title: planName,
    description: subjectText,
    organizationId: companyId,
    createdBy: data.CreatedBy || '',
    createdByName: data.CreatedByName || '',
    date: dateStr,
    participants,
    subjects,
    notes: data.content || '',
    status,
    createdAt: data.CreatedDate?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
    updatedAt: data.UpdatedDate?.toDate?.()?.toISOString() || data.updatedAt || new Date().toISOString(),
  };
}

/**
 * Convert a React BattlePlan back to Vue Firestore format for writes.
 */
function mapPlanToVueDoc(plan: Partial<BattlePlan>): Record<string, any> {
  const vueDoc: Record<string, any> = {};

  if (plan.title !== undefined) {
    vueDoc['Document'] = { Name: plan.title };
  }

  if (plan.description !== undefined) {
    vueDoc['Subject'] = plan.description;
  }

  if (plan.date !== undefined) {
    // YYYY-MM-DD -> DD-MM-YYYY
    const parts = plan.date.split('-');
    if (parts.length === 3) {
      vueDoc['Date'] = `${parts[2]}-${parts[1]}-${parts[0]}`;
    } else {
      vueDoc['Date'] = plan.date;
    }
  }

  if (plan.participants !== undefined) {
    vueDoc['Participants'] = plan.participants.map(p => ({
      Name: p.name,
      ...(p.email && { Email: p.email }),
      ...(p.role && { Role: p.role }),
      ...(p.id && { id: p.id }),
    }));
  }

  if (plan.subjects !== undefined) {
    // Flatten all action items from all subjects
    const allActionItems = plan.subjects.flatMap(s => s.actionItems);
    vueDoc['ActionItems'] = allActionItems.map(item => ({
      Name: item.description,
      completed: item.isCompleted,
      ...(item.id && { id: item.id }),
      ...(item.assigneeName && { AssigneeName: item.assigneeName }),
      ...(item.assigneeId && { AssigneeId: item.assigneeId }),
      ...(item.dueDate && { DueDate: item.dueDate }),
      ...(item.priority && { Priority: item.priority }),
      ...(item.notes && { Notes: item.notes }),
    }));
  }

  if (plan.notes !== undefined) {
    vueDoc['content'] = plan.notes;
  }

  if (plan.status !== undefined) {
    // Map status to Vue numeric format
    const statusMap: Record<string, number> = {
      draft: 0,
      active: 1,
      completed: 2,
      archived: 3,
    };
    vueDoc['Status'] = statusMap[plan.status] ?? 1;
  }

  return vueDoc;
}

export function PlanningProvider({ children }: { children: ReactNode }) {
  const { simpleUser, isAuthenticated } = useSimpleAuth();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [plans, setPlans] = useState<BattlePlan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<BattlePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<PlanFilter>({});

  // Calculate stats
  const calculateStats = useCallback((plansList: BattlePlan[]): PlanningStats => {
    const now = new Date();
    let totalActionItems = 0;
    let completedActionItems = 0;
    let overdueActionItems = 0;

    plansList.forEach(plan => {
      plan.subjects.forEach(subject => {
        subject.actionItems.forEach(item => {
          totalActionItems++;
          if (item.isCompleted) {
            completedActionItems++;
          } else if (item.dueDate && new Date(item.dueDate) < now) {
            overdueActionItems++;
          }
        });
      });
    });

    return {
      totalPlans: plansList.length,
      activePlans: plansList.filter(p => p.status === 'active').length,
      completedPlans: plansList.filter(p => p.status === 'completed').length,
      totalActionItems,
      completedActionItems,
      overdueActionItems,
    };
  }, []);

  const [stats, setStats] = useState<PlanningStats>({
    totalPlans: 0,
    activePlans: 0,
    completedPlans: 0,
    totalActionItems: 0,
    completedActionItems: 0,
    overdueActionItems: 0,
  });

  useEffect(() => {
    setStats(calculateStats(plans));
  }, [plans, calculateStats]);

  // Load companies where user email is in Users array (same pattern as budget app)
  useEffect(() => {
    if (!isAuthenticated || !simpleUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const companiesRef = collection(db, 'companies');
    const q = query(companiesRef, where('Users', 'array-contains', simpleUser.email));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const companiesList = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id,
      })) as Company[];

      setCompanies(companiesList);

      // Auto-select first company
      if (!currentCompany && companiesList.length > 0) {
        setCurrentCompany(companiesList[0]);
      }

      setLoading(false);
    }, (err) => {
      console.error('Error loading companies:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isAuthenticated, simpleUser?.email]);

  // Load battle plans from companies/{companyId}/BattlePlans
  useEffect(() => {
    if (!currentCompany?.id) {
      setPlans([]);
      setCurrentPlan(null);
      return;
    }

    const battlePlansRef = collection(db, 'companies', currentCompany.id, 'BattlePlans');

    const unsubscribe = onSnapshot(battlePlansRef, (snapshot) => {
      const plansList = snapshot.docs.map(d =>
        mapVueDocToPlan(d.id, d.data(), currentCompany.id)
      );

      // Sort by date descending
      plansList.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return b.date.localeCompare(a.date);
      });

      setPlans(plansList);
    }, (err) => {
      console.error('Error loading battle plans:', err);
      setError('Failed to load plans');
    });

    return () => unsubscribe();
  }, [currentCompany?.id]);

  const selectCompany = useCallback((companyId: string) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setCurrentCompany(company);
      setCurrentPlan(null);
    }
  }, [companies]);

  // Filter plans
  const filteredPlans = useMemo(() => {
    return plans.filter(plan => {
      if (filter.status && plan.status !== filter.status) return false;
      if (filter.dateFrom && plan.date < filter.dateFrom) return false;
      if (filter.dateTo && plan.date > filter.dateTo) return false;
      if (filter.participantId && !plan.participants.some(p => p.id === filter.participantId)) return false;
      if (filter.searchQuery) {
        const q = filter.searchQuery.toLowerCase();
        return (
          plan.title.toLowerCase().includes(q) ||
          plan.description?.toLowerCase().includes(q) ||
          plan.subjects.some(s => s.title.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [plans, filter]);

  const fetchPlans = useCallback(async () => {
    // Plans are loaded via onSnapshot, this is a no-op for compatibility
  }, []);

  const fetchPlan = useCallback(async (id: string): Promise<BattlePlan | null> => {
    // Plans are loaded via onSnapshot, just find in local state
    const plan = plans.find(p => p.id === id) || null;
    setCurrentPlan(plan);
    return plan;
  }, [plans]);

  const createPlan = useCallback(async (
    planData: Omit<BattlePlan, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'createdBy' | 'createdByName'>
  ): Promise<string> => {
    if (!simpleUser || !currentCompany?.id) throw new Error('Not authenticated or no company selected');

    const id = generateId();
    const now = new Date().toISOString();

    const fullPlan: BattlePlan = {
      ...planData,
      id,
      organizationId: currentCompany.id,
      createdBy: simpleUser.uid,
      createdByName: simpleUser.displayName || simpleUser.email || 'Unknown',
      createdAt: now,
      updatedAt: now,
    };

    const vueDoc = mapPlanToVueDoc(fullPlan);
    vueDoc['CreatedBy'] = simpleUser.uid;
    vueDoc['CreatedByName'] = simpleUser.displayName || simpleUser.email || '';
    vueDoc['CreatedDate'] = serverTimestamp();
    vueDoc['UpdatedDate'] = serverTimestamp();
    vueDoc['_Data'] = '';

    await setDoc(doc(db, 'companies', currentCompany.id, 'BattlePlans', id), vueDoc);
    return id;
  }, [simpleUser, currentCompany?.id]);

  const updatePlan = useCallback(async (id: string, updates: Partial<BattlePlan>) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    // If updating subjects/action items, we need the full plan to rebuild
    const plan = plans.find(p => p.id === id);
    if (!plan) throw new Error('Plan not found');

    const merged = { ...plan, ...updates };
    const vueDoc = mapPlanToVueDoc(merged);
    vueDoc['UpdatedDate'] = serverTimestamp();

    await setDoc(
      doc(db, 'companies', currentCompany.id, 'BattlePlans', id),
      vueDoc,
      { merge: true }
    );

    // Update current plan if it's the one being edited
    if (currentPlan?.id === id) {
      setCurrentPlan(prev => prev ? { ...prev, ...updates } : null);
    }
  }, [currentCompany?.id, plans, currentPlan]);

  const deletePlan = useCallback(async (id: string) => {
    if (!currentCompany?.id) throw new Error('No company selected');

    await deleteDoc(doc(db, 'companies', currentCompany.id, 'BattlePlans', id));
    if (currentPlan?.id === id) {
      setCurrentPlan(null);
    }
  }, [currentCompany?.id, currentPlan]);

  // Subject operations
  const addSubject = useCallback(async (
    planId: string,
    subject: Omit<PlanSubject, 'id' | 'actionItems'>
  ) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const newSubject: PlanSubject = {
      ...subject,
      id: generateId(),
      actionItems: [],
    };

    const updatedSubjects = [...plan.subjects, newSubject];
    await updatePlan(planId, { subjects: updatedSubjects });
  }, [plans, updatePlan]);

  const updateSubject = useCallback(async (
    planId: string,
    subjectId: string,
    updates: Partial<PlanSubject>
  ) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const updatedSubjects = plan.subjects.map(s =>
      s.id === subjectId ? { ...s, ...updates } : s
    );
    await updatePlan(planId, { subjects: updatedSubjects });
  }, [plans, updatePlan]);

  const deleteSubject = useCallback(async (planId: string, subjectId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const updatedSubjects = plan.subjects.filter(s => s.id !== subjectId);
    await updatePlan(planId, { subjects: updatedSubjects });
  }, [plans, updatePlan]);

  // Action Item operations
  const addActionItem = useCallback(async (
    planId: string,
    subjectId: string,
    item: Omit<ActionItem, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const now = new Date().toISOString();
    const newItem: ActionItem = {
      ...item,
      id: generateId(),
      createdAt: now,
      updatedAt: now,
    };

    const updatedSubjects = plan.subjects.map(s => {
      if (s.id === subjectId) {
        return { ...s, actionItems: [...s.actionItems, newItem] };
      }
      return s;
    });

    await updatePlan(planId, { subjects: updatedSubjects });
  }, [plans, updatePlan]);

  const updateActionItem = useCallback(async (
    planId: string,
    subjectId: string,
    itemId: string,
    updates: Partial<ActionItem>
  ) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const updatedSubjects = plan.subjects.map(s => {
      if (s.id === subjectId) {
        return {
          ...s,
          actionItems: s.actionItems.map(item =>
            item.id === itemId
              ? { ...item, ...updates, updatedAt: new Date().toISOString() }
              : item
          ),
        };
      }
      return s;
    });

    await updatePlan(planId, { subjects: updatedSubjects });
  }, [plans, updatePlan]);

  const deleteActionItem = useCallback(async (
    planId: string,
    subjectId: string,
    itemId: string
  ) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const updatedSubjects = plan.subjects.map(s => {
      if (s.id === subjectId) {
        return {
          ...s,
          actionItems: s.actionItems.filter(item => item.id !== itemId),
        };
      }
      return s;
    });

    await updatePlan(planId, { subjects: updatedSubjects });
  }, [plans, updatePlan]);

  const toggleActionItem = useCallback(async (
    planId: string,
    subjectId: string,
    itemId: string
  ) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const subject = plan.subjects.find(s => s.id === subjectId);
    const item = subject?.actionItems.find(i => i.id === itemId);
    if (!item) return;

    const updates: Partial<ActionItem> = {
      isCompleted: !item.isCompleted,
      completedAt: !item.isCompleted ? new Date().toISOString() : undefined,
    };

    await updateActionItem(planId, subjectId, itemId, updates);
  }, [plans, updateActionItem]);

  // Participant operations
  const addParticipant = useCallback(async (
    planId: string,
    participant: Omit<Participant, 'id'>
  ) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const newParticipant: Participant = {
      ...participant,
      id: generateId(),
    };

    const updatedParticipants = [...plan.participants, newParticipant];
    await updatePlan(planId, { participants: updatedParticipants });
  }, [plans, updatePlan]);

  const removeParticipant = useCallback(async (planId: string, participantId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const updatedParticipants = plan.participants.filter(p => p.id !== participantId);
    await updatePlan(planId, { participants: updatedParticipants });
  }, [plans, updatePlan]);

  const value: PlanningContextType = {
    companies,
    currentCompany,
    selectCompany,
    plans: filteredPlans,
    currentPlan,
    loading,
    error,
    stats,
    filter,
    setFilter,
    fetchPlans,
    fetchPlan,
    createPlan,
    updatePlan,
    deletePlan,
    setCurrentPlan,
    addSubject,
    updateSubject,
    deleteSubject,
    addActionItem,
    updateActionItem,
    deleteActionItem,
    toggleActionItem,
    addParticipant,
    removeParticipant,
  };

  return (
    <PlanningContext.Provider value={value}>
      {children}
    </PlanningContext.Provider>
  );
}

export function usePlanning() {
  const context = useContext(PlanningContext);
  if (context === undefined) {
    throw new Error('usePlanning must be used within a PlanningProvider');
  }
  return context;
}
